import React, { useEffect, useState } from "react";
import {
  Box, Grid, Typography, Button, TextField, InputAdornment, Paper, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  Tooltip, alpha, MenuItem, Select, FormControl,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TvIcon from "@mui/icons-material/Tv";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  NAVY, NAVY_DARK, GOLD, GOLD_SOFT, PAGE_BG, BORDER,
  TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";
import {
  useGetDevicesQuery, useDeleteDeviceMutation, useGetCompanyQuery,
} from "../../../Api/attoDeskApi";
import { IDevice } from "../../../Api/Interface/api.interface";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import PageLoader from "../../../Components/Loader/PageLoader";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNotifier } from "../../../Core/Notifier";
import Pagination from "../../../Components/Pagination";
import ActionButtons from "../../../Components/ui/ActionButtons";

const formatLastSeen = (iso: string | null): string => {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)} day${Math.floor(hrs / 24) === 1 ? "" : "s"} ago`;
};

const STATUS_STYLE: Record<
  "online" | "offline" | "disabled",
  { label: string; bg: string; fg: string; border: string; dot: string }
> = {
  online:   { label: "Online",   bg: "#ecfdf5", fg: "#047857", border: "#a7f3d0", dot: "#10b981" },
  offline:  { label: "Offline",  bg: "#fef2f2", fg: "#b91c1c", border: "#fecaca", dot: "#ef4444" },
  disabled: { label: "Disabled", bg: "#f1f5f9", fg: "#475569", border: "#cbd5e1", dot: "#94a3b8" },
};

const StatusPill: React.FC<{ status: "online" | "offline" | "disabled" }> = ({ status }) => {
  const s = STATUS_STYLE[status];
  return (
    <Chip
      size="small"
      icon={<FiberManualRecordIcon sx={{ fontSize: 9 }} />}
      label={s.label}
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.02em",
        bgcolor: s.bg,
        color: s.fg,
        border: `1px solid ${s.border}`,
        "& .MuiChip-icon": { color: s.dot, ml: 0.75 },
      }}
    />
  );
};

const exportToCsv = (rows: IDevice[]) => {
  if (rows.length === 0) return;
  const head = [
    "Code", "Name", "Location", "Status", "Playlist Group",
    "Last Seen", "App Version",
  ].join(",");
  const body = rows.map(r => [
    r.device_code, r.device_name, r.location ?? "", r.status,
    r.playlist?.name ?? "", r.last_seen_at ?? "", r.app_version ?? "",
  ].map(c => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([head + "\n" + body], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `devices-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const DeviceList: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();

  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const debounced = useDebounce(searchQuery, 300);

  const [statusFilter, setStatusFilter] = useState<"" | "online" | "offline" | "disabled">("");
  const [companyFilter, setCompanyFilter] = useState<number | "">("");
  const [showFilters, setShowFilters] = useState(false);

  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);

  const { data, isLoading, isError, refetch } = useGetDevicesQuery({
    searchText: currentSearchQuery,
  });
  const [deleteDevice] = useDeleteDeviceMutation();

  // Super admin gets a Company filter; tenant users are already scoped server-side.
  const { data: companiesData } = useGetCompanyQuery(
    { searchText: "", id: "" } as any,
    { skip: !isSuperAdmin }
  );
  const companies: Array<{ id: number; companyName: string }> = Array.isArray(
    (companiesData as any)?.data
  )
    ? ((companiesData as any).data as Array<{ id: number; companyName: string }>)
    : [];

  const [openDelete, setOpenDelete] = useState(false);
  const [toDelete, setToDelete] = useState<IDevice | null>(null);

  useEffect(() => {
    if (debounced.length >= 2 || debounced === "") setCurrentSearchQuery(debounced);
  }, [debounced]);

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await deleteDevice(toDelete.id).unwrap();
      if (res.status) {
        showMessage("Device deleted");
        setOpenDelete(false); setToDelete(null);
        refetch();
      } else showErrorMessage("Failed to delete device");
    } catch {
      showErrorMessage("Failed to delete device");
    }
  };

  if (isLoading) return <PageLoader height="20vh" />;

  const devices: IDevice[] = Array.isArray(data?.data)
    ? ((data?.data as unknown) as IDevice[])
    : [];

  const filtered = devices.filter(d => {
    if (statusFilter && d.status !== statusFilter) return false;
    if (companyFilter && d.company_id !== companyFilter) return false;
    return true;
  });

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* ── Top Bar ── */}
      <Box sx={{ bgcolor: "#fff", borderBottom: `1px solid ${BORDER}`, px: 3, py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 3,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
            boxShadow: `0 6px 18px ${alpha(NAVY, 0.35)}`,
            border: `1px solid ${alpha(GOLD, 0.35)}`,
          }}>
            <TvIcon sx={{ fontSize: 22, color: GOLD }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={22} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
              Devices
            </Typography>
            <Typography fontSize={12} color={TEXT_SECONDARY} fontWeight={400}>
              {devices.length} device{devices.length === 1 ? "" : "s"} registered
            </Typography>
          </Box>
          <TextField
            size="small"
            placeholder="Search by code, name or location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: TEXT_SECONDARY }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2, fontSize: 13, bgcolor: "#fff",
                "& fieldset": { borderColor: BORDER },
                "&:hover fieldset": { borderColor: alpha(GOLD, 0.6) },
                "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: 1.5 },
              },
            }}
          />
          <Button
            variant="outlined" size="small"
            onClick={() => setShowFilters(v => !v)}
            startIcon={<FilterListIcon sx={{ fontSize: 15 }} />}
            sx={{
              borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 500,
              borderColor: showFilters ? GOLD : BORDER,
              color: showFilters ? NAVY : TEXT_PRIMARY,
              bgcolor: showFilters ? alpha(GOLD, 0.08) : "transparent",
              "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.08), color: NAVY },
            }}
          >
            Filters
          </Button>
          <Divider orientation="vertical" flexItem sx={{ borderColor: BORDER }} />
          <Button
            variant="outlined" size="small"
            onClick={() => exportToCsv(filtered)}
            startIcon={<FileDownloadIcon sx={{ fontSize: 15 }} />}
            sx={{
              borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 500,
              borderColor: BORDER, color: TEXT_PRIMARY,
              "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.08), color: NAVY },
            }}
          >
            Export
          </Button>
          <Button
            variant="contained" color="secondary" disableElevation size="small"
            onClick={() => navigate("/device/create")}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 13, fontWeight: 700, px: 3,
              color: NAVY_DARK,
              background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
              boxShadow: `0 4px 14px ${alpha(GOLD, 0.45)}`,
              "&:hover": {
                background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)`,
                boxShadow: `0 6px 20px ${alpha(GOLD, 0.55)}`,
                transform: "translateY(-1px)",
              },
            }}
          >
            Add Device
          </Button>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ p: 3 }}>
        {showFilters && (
          <Paper elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: 2.5, border: `1px solid ${BORDER}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <FilterListIcon sx={{ fontSize: 18, color: GOLD }} />
              <Typography fontSize={13} fontWeight={700} color={NAVY}>
                Filters
              </Typography>
            </Box>
            <Grid container spacing={2} alignItems="flex-end">
              {isSuperAdmin && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography sx={{ fontSize: 11, fontWeight: 500, color: TEXT_SECONDARY, mb: 0.75 }}>
                    Company
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={companyFilter === "" ? "" : String(companyFilter)}
                      onChange={(e) =>
                        setCompanyFilter(e.target.value === "" ? "" : Number(e.target.value))
                      }
                      displayEmpty
                      sx={{ fontSize: 13, borderRadius: 1.5 }}
                    >
                      <MenuItem value="">All companies</MenuItem>
                      {companies.map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>
                          {c.companyName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: TEXT_SECONDARY, mb: 0.75 }}>
                  Status
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {[
                    { v: "", label: "All" },
                    { v: "online", label: "Online" },
                    { v: "offline", label: "Offline" },
                    { v: "disabled", label: "Disabled" },
                  ].map(opt => (
                    <Button
                      key={opt.v}
                      size="small"
                      onClick={() => setStatusFilter(opt.v as any)}
                      sx={{
                        textTransform: "none",
                        fontSize: 12,
                        fontWeight: 600,
                        borderRadius: 1.5,
                        px: 1.75,
                        borderColor: statusFilter === opt.v ? GOLD : BORDER,
                        bgcolor: statusFilter === opt.v ? alpha(GOLD, 0.08) : "transparent",
                        color: statusFilter === opt.v ? NAVY : TEXT_SECONDARY,
                        border: "1px solid",
                        "&:hover": { borderColor: GOLD, color: NAVY, bgcolor: alpha(GOLD, 0.08) },
                      }}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* ── Table ── */}
        <Paper elevation={0} sx={{
          borderRadius: "12px", border: `1px solid ${BORDER}`,
          overflow: "hidden", boxShadow: "0 4px 12px rgba(11,30,45,0.06)",
        }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {(
                    [
                      "Code",
                      "Device",
                      ...(isSuperAdmin ? ["Company"] : []),
                      "Location",
                      "Status",
                      "Playlist Group",
                      "Last Seen",
                      "Actions",
                    ] as string[]
                  ).map((h, i, arr) => (
                    <TableCell
                      key={h}
                      sx={{
                        borderBottom: `1px solid ${BORDER}`,
                        background: `linear-gradient(180deg, ${alpha(NAVY, 0.06)} 0%, #f8fafc 100%)`,
                        fontSize: 11,
                        fontWeight: 700,
                        color: NAVY,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        py: 1.5,
                        textAlign: i === arr.length - 1 ? "right" : "left",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isError || filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isSuperAdmin ? 8 : 7} align="center" sx={{ py: 6, color: TEXT_SECONDARY }}>
                      {isError ? "Error fetching data" : "No devices registered yet"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          transition: "background 0.15s ease",
                          "&:hover": { bgcolor: "#f8fafc" },
                        }}
                      >
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}` }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, letterSpacing: "0.02em" }}>
                            {row.device_code}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}` }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY }}>
                            {row.device_name}
                          </Typography>
                          {row.app_version && (
                            <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                              v{row.app_version}
                            </Typography>
                          )}
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell sx={{ borderBottom: `1px solid ${BORDER}` }}>
                            {row.company ? (
                              <Chip
                                size="small"
                                label={row.company.name}
                                sx={{
                                  height: 22,
                                  fontSize: 11,
                                  fontWeight: 600,
                                  bgcolor: alpha(NAVY, 0.06),
                                  color: NAVY,
                                  border: `1px solid ${alpha(NAVY, 0.12)}`,
                                }}
                              />
                            ) : (
                              <Typography component="span" sx={{ fontSize: 13, color: TEXT_SECONDARY }}>—</Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, fontSize: 13, color: TEXT_PRIMARY }}>
                          {row.location || <Typography component="span" sx={{ color: TEXT_SECONDARY }}>—</Typography>}
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}` }}>
                          <StatusPill status={row.status} />
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}` }}>
                          {row.playlist_group ? (
                            <Box>
                              <Typography sx={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY }}>
                                {row.playlist_group.name}
                              </Typography>
                              <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                                {row.playlist_group.playback_mode}
                              </Typography>
                            </Box>
                          ) : row.playlist ? (
                            <Box>
                              <Typography sx={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY }}>
                                {row.playlist.name}
                              </Typography>
                              <Typography sx={{ fontSize: 11, color: "#b91c1c" }}>
                                legacy single playlist
                              </Typography>
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>—</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, fontSize: 13, color: TEXT_PRIMARY }}>
                          <Tooltip title={row.last_seen_at ? new Date(row.last_seen_at).toLocaleString() : "No heartbeat received"}>
                            <span>{formatLastSeen(row.last_seen_at)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid ${BORDER}`, textAlign: "right" }}>
                          <ActionButtons
                            onView={() => navigate(`/device/view/${row.id}`)}
                            onEdit={() => navigate(`/device/edit/${row.id}`)}
                            onDelete={() => { setToDelete(row); setOpenDelete(true); }}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {filtered.length > rowsPerPage && (
            <Box sx={{ p: 2, borderTop: `1px solid ${BORDER}` }}>
              <Pagination
                count={filtered.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_e, p) => setPage(p)}
              />
            </Box>
          )}
        </Paper>
      </Box>

      <DeletePopup
        open={openDelete}
        handleCloseDelete={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        title="Delete Device"
        content={
          toDelete
            ? `Permanently delete device "${toDelete.device_name}" (${toDelete.device_code})?`
            : ""
        }
      />
    </Box>
  );
};

export default DeviceList;
